import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { validateProps } from '../../../../../utils/propValidator';
import { FormFieldWrapper } from '../../layout';

const propRules = {
  name: { type: 'string', required: true },
  value: { required: true },
  onChange: { type: 'function', required: true },

  options: { type: 'array', required: true },

  wrapperOrientation: { oneOf: ['row', 'column'] },
  orientation: { oneOf: ['row', 'column'] },

  label: { type: 'string' },
  disabled: { type: 'boolean' },

  error: { type: 'boolean' },
  errorMessage: { type: 'string' },
  helperText: { type: 'string' },
  showErrorInline: { type: 'boolean' },
  reserveErrorSpace: { type: 'boolean' },

  wrapperVariant: { type: 'object' },
  wrapperSx: { type: 'object' },
  sx: { type: 'object' },
};

/**
 * BaseRadioGroup
 *
 * Pure controlled radio group component.
 * Renders a list of radio options and emits the selected value.
 *
 * This component has no form awareness and must be controlled
 * by the parent via `value` and `onChange`.
 *
 * @param {Object} props
 * @param {string} props.name - Shared name attribute for all radio options in the group.
 * @param {*} props.value - Currently selected value.
 * @param {(value: any) => void} props.onChange - Called when a radio option is selected.
 * @param {Array<{ label: string, value: any, disabled?: boolean }>} props.options - List of radio options to render.
 * @param {string} [props.label] - Group label rendered above the radio options.
 * @param {boolean} [props.disabled=false] - Disables all radio options when true.
 * @param {boolean} [props.error=false] - Whether the group is in an error state.
 * @param {string} [props.errorMessage] - Error message rendered inline when `error` is true.
 * @param {string} [props.helperText] - Helper text rendered alongside the error.
 * @param {boolean} [props.showErrorInline=true] - Controls whether the error is rendered inline by this component.
 * @param {boolean} [props.reserveErrorSpace=false] - Reserves vertical space for error/helper text to prevent layout shift.
 * @param {'row'|'column'} [props.orientation='row'] - Layout orientation for the radio buttons
 * @param {'row'|'column'} [props.wrapperOrientation='column'] - Layout orientation for the Form Field Wrapper
 * @param {Object} [props.sx] - MUI sx styles applied to the radio list container.
 * @param {Object} [props.wrapperSx] - MUI sx styles applied to the FormFieldWrapper.
 * @param {Object} [props.wrapperVariant] - FormFieldWrapper label variant
 *
 * @returns {React.ReactElement}
 */
export default function BaseRadioGroup(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'BaseRadioGroup');

  const {
    name,
    value,
    onChange,
    options,

    label,
    disabled = false,

    orientation = 'row',
    wrapperOrientation,

    error = false,
    errorMessage,
    helperText,
    showErrorInline = true,
    reserveErrorSpace = false,

    sx = {},
    wrapperSx = {},
    wrapperVariant,
  } = props;

  return (
    <FormFieldWrapper
      label={label}
      error={showErrorInline ? error : false}
      errorMessage={showErrorInline ? errorMessage : undefined}
      helperText={helperText}
      reserveErrorSpace={reserveErrorSpace}
      sx={{ '& label': { color: 'text.primary' }, ...wrapperSx }}
      orientation={wrapperOrientation}
      labelVariant={wrapperVariant}
    >
      <Stack direction={orientation} gap={3} sx={sx}>
        {options.map((opt) => {
          const optionId = `${name}-${opt.value}`;
          return (
            <Stack
              key={opt.value}
              component='label'
              htmlFor={optionId}
              direction='row'
              gap={1}
              alignItems='center'
              sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              <Radio
                id={optionId}
                name={name}
                size='small'
                disableRipple
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
                disabled={disabled || opt.disabled}
                sx={{
                  padding: 0,
                  color: 'grey.500',
                  '&.Mui-checked': { color: 'grey.700' },
                  '&.Mui-disabled': { color: 'grey.700', opacity: 0.3 },
                }}
              />
              <Typography variant='body1' sx={{ whiteSpace: 'nowrap', color: 'grey.500' }}>
                {opt.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </FormFieldWrapper>
  );
}
